import { CheckboxDataGroup } from "components/components/CheckboxList/CheckboxList";
import {
  CheckboxDataState,
  CheckboxState,
  ChevronState,
} from "components/components/CheckboxList/CheckboxListContainer";
import React, { useReducer } from "react";

export enum ListClickType {
  CHECKBOX,
  CHEVRON,
}

export type ListClickEvent = {
  id: number;
  clickType: ListClickType;
};

// Sets the current checkbox's parent to checked/unchecked/indeterminate
// depending on all its children, and then recurses up the tree. This is done
// in-place.
const updateParentCheckboxStateToMatchChildren = (
  checkboxStates: CheckboxDataState[],
  checkboxId: number
): void => {
  const checkbox = checkboxStates[checkboxId];

  if (checkbox.parentId === null) {
    return;
  }

  const parent = checkboxStates[checkbox.parentId];
  const siblings = checkboxStates.filter(
    (checkboxState) => checkboxState.parentId === parent.id
  );

  if (siblings.every((s) => s.checkboxState === CheckboxState.CHECKED)) {
    parent.checkboxState = CheckboxState.CHECKED;
  } else if (
    siblings.every((s) => s.checkboxState === CheckboxState.UNCHECKED)
  ) {
    parent.checkboxState = CheckboxState.UNCHECKED;
  } else {
    parent.checkboxState = CheckboxState.INDETERMINATE;
  }

  updateParentCheckboxStateToMatchChildren(checkboxStates, parent.id);
};

const findAllCheckboxesWithSameName = (
  checkboxStates: CheckboxDataState[],
  checkboxId: number
): number[] => {
  const name = checkboxStates[checkboxId].name;
  return checkboxStates
    .filter((checkboxState) => checkboxState.name === name)
    .map((checkboxState) => checkboxState.id);
};

// Returns the new checkbox states given the current state and a click event.
export const updateCheckboxStatesOnClick = (
  currentCheckboxStates: CheckboxDataState[],
  clickEvent: ListClickEvent
): CheckboxDataState[] => {
  const newStates = currentCheckboxStates.map((i) => ({ ...i }));
  const clickedId = clickEvent.id;

  const mapToChildren = (id: number, f: (id: number) => void): void => {
    currentCheckboxStates
      .filter((i) => i.parentId === id)
      .forEach((child) => f(child.id));
  };

  const setUnchecked = (id: number): void => {
    const ids = findAllCheckboxesWithSameName(newStates, id);
    ids.forEach((i) => {
      newStates[i].checkboxState = CheckboxState.UNCHECKED;
      mapToChildren(i, setUnchecked);
      updateParentCheckboxStateToMatchChildren(newStates, i);
    });
  };

  const setChecked = (id: number): void => {
    const ids = findAllCheckboxesWithSameName(newStates, id);
    ids.forEach((i) => {
      newStates[i].checkboxState = CheckboxState.CHECKED;
      mapToChildren(i, setChecked);
      updateParentCheckboxStateToMatchChildren(newStates, i);
    });
  };

  const setClosed = (id: number): void => {
    newStates[id].chevronState = ChevronState.CLOSED;
    mapToChildren(id, setClosed);
  };

  const setOpened = (id: number): void => {
    newStates[id].chevronState = ChevronState.OPEN;
  };

  const closeAllChevrons = (): void => {
    newStates.forEach((e) => {
      e.chevronState = ChevronState.CLOSED;
    });
  };

  const openParentChevrons = (initialId: number): void => {
    let parentId: number | null = initialId;
    while (parentId) {
      newStates[parentId].chevronState = ChevronState.OPEN;
      parentId = newStates[parentId].parentId;
    }
  };

  switch (clickEvent.clickType) {
    case ListClickType.CHEVRON:
      if (newStates[clickedId].chevronState === ChevronState.OPEN) {
        setClosed(clickedId);
      } else {
        closeAllChevrons();
        setOpened(clickedId);
        openParentChevrons(clickedId);
      }
      break;
    case ListClickType.CHECKBOX:
      if (newStates[clickedId].checkboxState === CheckboxState.CHECKED) {
        setUnchecked(clickedId);
      } else {
        setChecked(clickedId);
      }
      break;
    default:
      break;
  }

  return newStates;
};

// Takes a definition of a checkbox list, sets the given checkboxes to be
// checked, and returns the new checkbox states.
export const getDefaultCheckboxStates = (
  checkboxListDefinition: CheckboxDataGroup[],
  defaultCheckedNames: string[]
): CheckboxDataState[] => {
  const defaultCheckboxStates = checkboxListDefinition.map((i) => ({
    ...i,
    checkboxState: defaultCheckedNames.includes(i.name)
      ? CheckboxState.CHECKED
      : CheckboxState.UNCHECKED,
    chevronState: ChevronState.CLOSED,
  }));

  for (const checkbox of defaultCheckboxStates) {
    updateParentCheckboxStateToMatchChildren(
      defaultCheckboxStates,
      checkbox.id
    );
  }
  return defaultCheckboxStates;
};

const useCheckboxList = (
  checkboxList: CheckboxDataGroup[],
  defaultChecked?: boolean
): [CheckboxDataState[], React.Dispatch<ListClickEvent>] => {
  const defaultCheckboxDataStates: CheckboxDataState[] =
    getDefaultCheckboxStates(
      checkboxList,
      defaultChecked ? checkboxList.map((i) => i.name) : []
    );

  const [CheckboxDataStates, registerClickForListItem] = useReducer(
    updateCheckboxStatesOnClick,
    defaultCheckboxDataStates
  );
  return [CheckboxDataStates, registerClickForListItem];
};

export default useCheckboxList;
